/**
 * RetryFollowupModal — shared confirmation modal (Story 7.14; also used by
 * 7.10b from ConversationDetail).
 *
 * Takes the target conversationId and fires `POST
 * /admin/conversations/:id/followups/retry`. Surfaces server-side discriminated
 * conflicts (409) as human-readable errors; happy path invalidates the
 * relevant query keys passed via `onSuccess`.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/sonner';
import { ApiError, apiClient } from '@/lib/api';

function humanizeError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: { message?: string } };
    return body?.error?.message ?? `${fallback} (HTTP ${err.status})`;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

export interface RetryFollowupModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conversationId: string;
  /** Query keys to invalidate after success. */
  invalidateKeys?: unknown[][];
}

export function RetryFollowupModal({
  open,
  onOpenChange,
  conversationId,
  invalidateKeys = [],
}: RetryFollowupModalProps) {
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => apiClient.post(`/admin/conversations/${conversationId}/followups/retry`),
    onSuccess: () => {
      toast.success('Follow-up reenviado.');
      onOpenChange(false);
      for (const key of invalidateKeys) {
        qc.invalidateQueries({ queryKey: key });
      }
    },
    onError: (err) => {
      toast.error(humanizeError(err, 'Falha ao reenviar follow-up.'));
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reenviar follow-up manualmente?</DialogTitle>
          <DialogDescription>
            Essa ação dispara um novo follow-up fora da cadência automática. Respeita janela Meta
            24h, opt-out e status da conversa.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Voltar
          </Button>
          <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending ? 'Enviando...' : 'Confirmar reenvio'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
